if (!window.CartProgressBar) {
  window.CartProgressBar = (() => {
    const WRAPPER_ID    = 'cart-progress-wrapper';
    const GIFT_PROP_KEY = '_cpb_gift_for_goal';

    let _mutating = false;

    // ─── Config ─────────────────────────────────────────────────────────────

    const readConfig = () => {
      const wrapper = document.getElementById(WRAPPER_ID);
      if (!wrapper) return null;

      const csv  = (str) => (str ? str.split(',').map((s) => s.trim()).filter(Boolean) : []);
      const pipe = (str) => (str ? str.split('||') : []);

      return {
        wrapper,
        thresholds:       csv(wrapper.dataset.thresholds).map(Number),
        preMessages:      pipe(wrapper.dataset.preGoalMessages),
        postMessages:     pipe(wrapper.dataset.postGoalMessages),
        goalBases:        csv(wrapper.dataset.goalBases),
        rewardTypes:      csv(wrapper.dataset.rewardTypes),
        giftVariantIds:   wrapper.dataset.giftVariantIds
                            ? wrapper.dataset.giftVariantIds.split(',').map((s) => s.trim())
                            : [],
        goalNumbers:      csv(wrapper.dataset.goalNumbers).map(Number),
        progressBarBasis: wrapper.dataset.progressBarBasis || 'item_price',
        currencyFormat:   wrapper.dataset.currencyFormat   || '',
        excludedIds:      csv(wrapper.dataset.excludedProductIds),
      };
    };

    // ─── Helpers ────────────────────────────────────────────────────────────

    const isAutoGift = (item) => {
      if (!item.properties) return false;
      return Object.prototype.hasOwnProperty.call(item.properties, GIFT_PROP_KEY);
    };

    const getGiftGoalNumber = (item) => {
      if (!isAutoGift(item)) return null;
      return parseInt(item.properties[GIFT_PROP_KEY], 10);
    };

    const computeMetrics = (items, excludedIds) => {
      let count = 0, subtotal = 0, total = 0;

      for (const item of items) {
        if (isAutoGift(item)) continue;
        if (excludedIds.includes(String(item.product_id))) continue;
        count    += item.quantity;
        subtotal += item.original_line_price;
        total    += item.line_price;
      }

      return { count, subtotal, total };
    };

    const getMetricForIndex = (i, config, metrics) => {
      if (config.progressBarBasis === 'item_count') return metrics.count;
      return (config.goalBases[i] || 'total') === 'subtotal' ? metrics.subtotal : metrics.total;
    };

    const formatMoney = (cents, format) => {
      const amount = Math.max(0, cents) / 100;

      return format
        .replace('{{amount}}',                                  amount.toFixed(2))
        .replace('{{amount_no_decimals}}',                      amount.toFixed(0))
        .replace('{{amount_with_comma_separator}}',             amount.toFixed(2).replace('.', ','))
        .replace('{{amount_no_decimals_with_comma_separator}}', amount.toFixed(0))
        .replace('{{amount_with_apostrophe_separator}}',        amount.toFixed(2).replace('.', "'"))
        .replace('{{amount_no_decimals_with_space_separator}}', amount.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ' '))
        .replace('{{amount_with_space_separator}}',             amount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ' ').replace('.', ','))
        .replace('{{amount_with_period_and_space_separator}}',  amount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ' '));
    };

    // ─── DOM ────────────────────────────────────────────────────────────────

    const updateDOM = (config, metrics, reachedFlags) => {
      const { wrapper, thresholds, preMessages, postMessages, progressBarBasis, currencyFormat } = config;

      let prev = 0;
      wrapper.querySelectorAll('.cpb-segment').forEach((segment, i) => {
        const fill = segment.querySelector('.cpb-segment-fill');
        if (!fill) return;

        const metric    = getMetricForIndex(i, config, metrics);
        const threshold = thresholds[i];
        const length    = threshold - prev;
        let percentage  = 0;

        if (metric >= threshold)  percentage = 100;
        else if (metric > prev)   percentage = Math.min(((metric - prev) / length) * 100, 100);

        fill.style.width = `${percentage}%`;
        prev = threshold;
      });

      const nextIndex = reachedFlags.findIndex((reached) => !reached);

      wrapper.querySelectorAll('.cpb-goal-icon').forEach((el, i) => {
        const reached = reachedFlags[i];
        el.classList.toggle('reached', reached);
        // Mark the immediate next goal for the pulse-ring animation
        el.classList.toggle('cpb-goal-icon--next', !reached && i === nextIndex);

        const img = el.querySelector('img.cpb-icon-img');
        if (!img) return;

        img.src = reached
          ? (el.dataset.reachedIcon || el.dataset.regularIcon)
          : el.dataset.regularIcon;
      });

      wrapper.classList.toggle('full', reachedFlags.every(Boolean));

      const messageEl = wrapper.querySelector('.cpb-message');
      if (!messageEl) return;

      if (nextIndex === -1) {
        messageEl.innerHTML = postMessages[postMessages.length - 1] || '';
        return;
      }

      const remaining = Math.max(0, thresholds[nextIndex] - getMetricForIndex(nextIndex, config, metrics));
      const formatted = progressBarBasis === 'item_count'
        ? String(remaining)
        : formatMoney(remaining, currencyFormat);

      messageEl.innerHTML = (preMessages[nextIndex] || '').replace('[remaining_for_goal]', formatted);
    };

    // ─── Cart API ───────────────────────────────────────────────────────────

    const cartAdd = (variantId, quantity, properties) =>
      fetch('/cart/add.js', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body:    JSON.stringify({ items: [{ id: Number(variantId), quantity, properties }] }),
      })
        .then((response) => response.json())
        .catch((error) => { console.error('[CPB] cartAdd error:', error); return null; });

    const cartChange = (line, quantity) =>
      fetch('/cart/change.js', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body:    JSON.stringify({ line, quantity }),
      })
        .then((response) => response.json())
        .catch((error) => { console.error('[CPB] cartChange error:', error); return null; });

    const refreshCartUI = async () => {
      const isDrawer = !!document.querySelector('cart-drawer-items');

      // Fetch both sections we need in one request using the sections array approach
      // Same as Dawn's updateQuantity — use /cart/change.js with sections param
      // but since we have no pending change, use /cart/update.js with empty updates
      // to get fresh rendered section HTML + item_count in one shot
      const sectionsNeeded = isDrawer
        ? ['cart-drawer', 'cart-icon-bubble']
        : ['main-cart-items', 'cart-icon-bubble'];

      let parsedState;
      try {
        const response = await fetch('/cart/update.js', {
          method:  'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body:    JSON.stringify({
            updates:      {},
            sections:     sectionsNeeded,
            sections_url: window.location.pathname,
          }),
        });
        parsedState = await response.json();
      } catch (error) {
        console.error('[CPB] refreshCartUI fetch error:', error);
        return;
      }

      const itemCount = parsedState.item_count ?? 0;
      const isEmpty   = itemCount === 0;

      const getSectionInnerHTML = (html, selector) =>
        new DOMParser().parseFromString(html, 'text/html').querySelector(selector)?.innerHTML ?? '';

      if (isDrawer) {
        // Mirror exactly what CartDrawerItems.updateQuantity does:
        // Replace .drawer__inner innerHTML inside #CartDrawer
        const cartDrawerSection = parsedState.sections?.['cart-drawer'];
        if (cartDrawerSection) {
          const cartDrawerEl = document.getElementById('CartDrawer');
          if (cartDrawerEl) {
            const drawerInner = cartDrawerEl.querySelector('.drawer__inner');
            if (drawerInner) {
              drawerInner.innerHTML = getSectionInnerHTML(cartDrawerSection, '.drawer__inner');
            }
          }
        }

        // Toggle is-empty on <cart-drawer> — exactly as Dawn's updateQuantity does
        // This is what shows/hides .drawer__inner-empty (the "Your cart is empty" block)
        const cartDrawerWrapper = document.querySelector('cart-drawer');
        if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', isEmpty);

        // Toggle is-empty on cart-drawer-items (re-query after innerHTML replacement above)
        const cartDrawerItems = document.querySelector('cart-drawer-items');
        if (cartDrawerItems) cartDrawerItems.classList.toggle('is-empty', isEmpty);

        // If empty, Dawn traps focus on .drawer__inner-empty
        if (isEmpty && cartDrawerWrapper) {
          const emptyEl = cartDrawerWrapper.querySelector('.drawer__inner-empty');
          const linkEl  = emptyEl?.querySelector('a');
          if (emptyEl && typeof trapFocus === 'function') trapFocus(emptyEl, linkEl);
        }
      } else {
        // Cart page — replace .js-contents of main-cart-items and main-cart-footer
        const cartItemsSection = parsedState.sections?.[document.getElementById('main-cart-items')?.dataset.id];
        const cartFooterSection = parsedState.sections?.[document.getElementById('main-cart-footer')?.dataset.id];

        if (cartItemsSection) {
          const mainCartItems = document.getElementById('main-cart-items');
          if (mainCartItems) {
            const jsContents = mainCartItems.querySelector('.js-contents') || mainCartItems;
            jsContents.innerHTML = getSectionInnerHTML(cartItemsSection, '.js-contents');
          }
        }
        if (cartFooterSection) {
          const mainCartFooter = document.getElementById('main-cart-footer');
          if (mainCartFooter) {
            const jsContents = mainCartFooter.querySelector('.js-contents') || mainCartFooter;
            jsContents.innerHTML = getSectionInnerHTML(cartFooterSection, '.js-contents');
          }
        }

        const cartItems  = document.querySelector('cart-items');
        const cartFooter = document.getElementById('main-cart-footer');
        if (cartItems)  cartItems.classList.toggle('is-empty', isEmpty);
        if (cartFooter) cartFooter.classList.toggle('is-empty', isEmpty);
      }

      // Always update cart-icon-bubble (the cart count in the header)
      // This is what makes the bubble count update without a page refresh
      const bubbleSection = parsedState.sections?.['cart-icon-bubble'];
      if (bubbleSection) {
        const bubbleEl = document.getElementById('cart-icon-bubble');
        if (bubbleEl) {
          bubbleEl.innerHTML = getSectionInnerHTML(bubbleSection, '.shopify-section');
        }
      }
    };

    // ─── Gift sync ──────────────────────────────────────────────────────────

    const syncGifts = async (config, reachedFlags) => {
      const { rewardTypes, giftVariantIds, goalNumbers } = config;

      let liveItems;
      try {
        const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
        const data     = await response.json();
        liveItems      = data.items || [];
      } catch (error) {
        console.error('[CPB] syncGifts: failed to fetch cart:', error);
        return false;
      }

      const toAdd    = [];
      const toRemove = [];

      for (let i = 0; i < reachedFlags.length; i++) {
        const rewardType = rewardTypes[i] || '';
        if (rewardType !== 'free_gift') continue;

        const variantId = giftVariantIds[i] || '';
        const goalNum   = goalNumbers[i];
        const reached   = reachedFlags[i];

        if (!variantId) {
          console.warn(`[CPB] Goal ${goalNum}: reward type is free_gift but no variant ID set.`);
          continue;
        }

        const existingItem = liveItems.find(
          (item) => isAutoGift(item) && getGiftGoalNumber(item) === goalNum
        );

        if (reached && !existingItem) {
          toAdd.push({ variantId, goalNum });
        } else if (!reached && existingItem) {
          toRemove.push({ lineIndex: liveItems.indexOf(existingItem) + 1, goalNum });
        }
      }

      if (toAdd.length === 0 && toRemove.length === 0) return false;

      toRemove.sort((a, b) => b.lineIndex - a.lineIndex);
      for (const { lineIndex, goalNum } of toRemove) {
        console.log(`[CPB] Removing gift for goal ${goalNum} at line ${lineIndex}`);
        await cartChange(lineIndex, 0);
      }

      for (const { variantId, goalNum } of toAdd) {
        console.log(`[CPB] Adding gift for goal ${goalNum}, variantId: ${variantId}`);
        const result = await cartAdd(variantId, 1, { [GIFT_PROP_KEY]: String(goalNum) });
        if (result?.status === 422) console.error(`[CPB] cartAdd failed for goal ${goalNum}:`, result);
      }

      return true;
    };

    // ─── Core ───────────────────────────────────────────────────────────────

    const updateProgressBar = async (parsedState) => {
      if (_mutating) return;

      const config = readConfig();
      if (!config || !config.thresholds.length) return;

      const items     = parsedState.items || [];
      const realItems = items.filter((item) => !isAutoGift(item));
      const metrics   = computeMetrics(items, config.excludedIds);

      const reachedFlags = realItems.length === 0
        ? config.thresholds.map(() => false)
        : config.thresholds.map((threshold, i) => getMetricForIndex(i, config, metrics) >= threshold);

      if (realItems.length === 0) {
        config.wrapper.style.display = 'none';
        _mutating = true;
        try {
          const mutated = await syncGifts(config, reachedFlags);
          if (mutated) await refreshCartUI();
        } finally {
          _mutating = false;
        }
        return;
      }

      config.wrapper.style.display = '';
      updateDOM(config, metrics, reachedFlags);

      _mutating = true;
      try {
        const mutated = await syncGifts(config, reachedFlags);
        if (!mutated) return;

        await refreshCartUI();

        const freshResponse = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
        const freshCart     = await freshResponse.json();
        const freshReal     = (freshCart.items || []).filter((item) => !isAutoGift(item));

        if (freshReal.length === 0) return;

        const freshConfig = readConfig();
        if (!freshConfig) return;

        const freshMetrics = computeMetrics(freshCart.items, freshConfig.excludedIds);
        const freshFlags   = freshConfig.thresholds.map((t, i) => getMetricForIndex(i, freshConfig, freshMetrics) >= t);

        updateDOM(freshConfig, freshMetrics, freshFlags);
      } finally {
        _mutating = false;
      }
    };

    // ─── Init ───────────────────────────────────────────────────────────────

    const refresh = () => {
      fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then((response) => response.json())
        .then((cart) => updateProgressBar({ items: cart.items }))
        .catch((error) => console.error('[CPB] refresh error:', error));
    };

    const init = () => {
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (event?.cartData?.items) {
            updateProgressBar({ items: event.cartData.items });
          } else {
            refresh();
          }
        });
      }

      refresh();
    };

    return { init, updateProgressBar, refresh };
  })();

  document.readyState === 'loading'
    ? document.addEventListener('DOMContentLoaded', () => window.CartProgressBar.init())
    : window.CartProgressBar.init();
}